import { MaskableGroupMemberProvider } from "@/components/form-items/CheckboxGroupMask";
import { FieldVisibilityToggle } from "@/components/form-items/FieldVisibilityToggle";
import { useMaskableFieldExpanded } from "@/components/form-items/MaskableFieldHeader";
import { useMaskableField } from "@/context/FormFieldVisibilityContext";
import { MASKABLE_FIELD_MINIMIZED_CLASS } from "@/lib/maskable-field-styles";
import { cn } from "@/lib/utils";
import { cloneElement, isValidElement, type ReactNode } from "react";

type TrailingElementProps = { disabled?: boolean };

function withTrailingDisabled(
  trailing: ReactNode,
  disabled: boolean,
): ReactNode {
  if (!disabled || !isValidElement<TrailingElementProps>(trailing)) {
    return trailing;
  }
  return cloneElement(trailing, {
    disabled: Boolean(trailing.props.disabled) || disabled,
  });
}

type MaskableTableSectionProps = {
  children: ReactNode;
  className?: string;
};

export function MaskableTableSection({
  children,
  className,
}: MaskableTableSectionProps) {
  return (
    <MaskableGroupMemberProvider>
      <div className={cn("flex w-full flex-col gap-2", className)}>
        {children}
      </div>
    </MaskableGroupMemberProvider>
  );
}

type MaskableTableSectionHeaderProps = {
  groupName: string;
  label: ReactNode;
  trailing?: ReactNode;
};

export function MaskableTableSectionHeader({
  groupName,
  label,
  trailing,
}: MaskableTableSectionHeaderProps) {
  const { enabled: maskEnabled } = useMaskableFieldExpanded(groupName);
  const { expanded, minimized, canToggle, toggleVisibility } =
    useMaskableField(groupName);

  // Masked → hidden entirely (re-enable it from the field visibility menu).
  if (maskEnabled && minimized) {
    return null;
  }

  return (
    <div className="flex items-baseline justify-between">
      <div className="flex min-w-0 items-center gap-1">
        <label
          className={cn(
            "text-sm font-medium text-gray-700",
            maskEnabled && minimized && MASKABLE_FIELD_MINIMIZED_CLASS,
          )}
        >
          {label}
        </label>
        {maskEnabled && canToggle && (
          <FieldVisibilityToggle
            expanded={expanded}
            minimized={minimized}
            onToggle={toggleVisibility}
          />
        )}
      </div>
      {withTrailingDisabled(trailing, maskEnabled && minimized)}
    </div>
  );
}

type MaskableTableSectionBodyProps = {
  groupName: string;
  children: ReactNode;
  className?: string;
};

export function MaskableTableSectionBody({
  groupName,
  children,
  className,
}: MaskableTableSectionBodyProps) {
  const { showControl, minimized } = useMaskableFieldExpanded(groupName);

  // Masked → hidden entirely rather than greyed/collapsed.
  if (minimized) {
    return null;
  }

  return (
    <div className={cn(!showControl && "hidden", className)}>{children}</div>
  );
}
